/* eslint no-param-reassign: 0 */
import _ from 'underscore';
import Promise from 'bluebird';
import URI from 'urijs';

import { http } from '@bingads-webui/http-util';

import AjaxHelper from './ajax-helper';
import { antiForgeryTokenKey, getAntiForgeryToken } from './anti-forgery-token';
import type {
  DataRecord,
  HttpOptions,
  RequestOptions,
  RestfulHttpModule,
} from './types';

const defaultSetting = {
  __adcenterAJAX: true,
  data: {},
  contentType: 'application/json',
  dataType: null as string | null,
  onsuccessEventData: {},
  traditional: false,
  silent: false,
};

function addAntiForgeryToken(options: RequestOptions) {
  const antiForgeryToken = getAntiForgeryToken();
  const method = _.result(options.method, 'toUpperCase');

  if (antiForgeryToken) {
    switch (options.contentType) {
      case 'application/json':
        options.headers = _.extend({}, options.headers, { [antiForgeryTokenKey]: antiForgeryToken });
        break;
      case 'application/x-www-form-urlencoded':
        if (method && method !== 'GET' && method !== 'DELETE') {
          options.data = _.extend({}, options.data, { [antiForgeryTokenKey]: antiForgeryToken });
        }
        break;
      case false:
        if (options.useFormDataFormat && options.data instanceof FormData && !options.data.get(antiForgeryTokenKey)) {
          options.data.append(antiForgeryTokenKey, antiForgeryToken);
        }
        break;
      default:
        break;
    }
  }
}

function appendQuery(uri: string, options: RequestOptions, defaultValue: DataRecord) {
  let param = _.isObject(options.params) ? options.params : {};

  param = _.defaults(param, defaultValue);
  return new URI(uri).addQuery(param).toString();
}

export default class {
  ajaxHelper: AjaxHelper;
  cidFetcher?: HttpOptions['cidFetcher'];
  uidFetcher?: HttpOptions['uidFetcher'];
  willSupportCors: boolean;
  constructor({
    cidFetcher,
    uidFetcher,
    ccuiAlertCenter,
    handleAjaxAuthFailure,
    appendErrorSummary,
    ajaxSuccess,
    willSupportCors = false,
  }: HttpOptions) {
    this.cidFetcher = cidFetcher;
    this.uidFetcher = uidFetcher;
    this.willSupportCors = willSupportCors;

    this.ajaxHelper = new AjaxHelper({
      ccuiAlertCenter,
      handleAjaxAuthFailure,
      appendErrorSummary,
      ajaxSuccess,
    });
  }

  ajax = (uri: string, options: RequestOptions, cb: Parameters<RestfulHttpModule['ajax']>[2]) => {
    const extendedOptions = _.defaults(
      {},
      options,
      defaultSetting
    );

    if (this.willSupportCors === true) {
      const defaultCorsHeaders = { 'X-Requested-With': 'XMLHttpRequest' };
      extendedOptions.headers = _.extend({}, defaultCorsHeaders, extendedOptions.headers);

      const defaultCorsXhrFields = { withCredentials: true };
      extendedOptions.xhrFields = _.extend({}, defaultCorsXhrFields, extendedOptions.xhrFields);
    }

    addAntiForgeryToken(extendedOptions);

    extendedOptions.beforeSend = this.ajaxHelper.beforeSend(extendedOptions);
    extendedOptions.success = this.ajaxHelper.success(extendedOptions);
    extendedOptions.error = this.ajaxHelper.error(extendedOptions);
    extendedOptions.complete = this.ajaxHelper.complete(extendedOptions);

    const cidUidQueryParam: DataRecord = {};
    if (_.isFunction(this.cidFetcher)) {
      const cid = this.cidFetcher();
      _.extend(cidUidQueryParam, { cid });
    }
    if (_.isFunction(this.uidFetcher)) {
      const uid = this.uidFetcher();
      _.extend(cidUidQueryParam, { uid });
    }
    // append cid and uid to the url
    const integrateUri = appendQuery(uri, extendedOptions, cidUidQueryParam);

    return (http as RestfulHttpModule).ajax(integrateUri, extendedOptions, cb)
      .then(res => (res.Errors ? Promise.reject(res) : Promise.resolve(res)));
  };

  defaults = (options: RequestOptions) => (...params: unknown[]) => {
    const [url, opt, callback] = (http as RestfulHttpModule).signature(params);
    const op = _.defaults({}, opt, options);

    return this.ajax(url, op, callback);
  };

  get = this.defaults({ method: 'get' });
  put = this.defaults({ method: 'put' });
  post = this.defaults({ method: 'post' });
  patch = this.defaults({ method: 'patch' });
  delete = this.defaults({ method: 'delete' });
}
